import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Truck, MapPin, Phone, ClockClockwise, SignOut, CurrencyDollar, Path } from "@phosphor-icons/react";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

/**
 * DriverPage — the entire cockpit for a first-party driver.
 *
 * Three tabs:
 *   1. My Deliveries — orders assigned to me, with Start / Complete
 *   2. Unassigned — pool of unclaimed deliveries drivers can self-assign
 *   3. History — completed today (with tips + mileage totals)
 */
const DriverPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [history, setHistory] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [earningsDays, setEarningsDays] = useState(7);
  const [completing, setCompleting] = useState(null);   // {order, tip, mileage}

  const load = async () => {
    try {
      const [m, u, h, e] = await Promise.all([
        axios.get(`${API}/delivery/orders/mine`),
        axios.get(`${API}/delivery/orders/unassigned`),
        axios.get(`${API}/delivery/driver/history?limit=50`),
        axios.get(`${API}/delivery/driver/earnings?days=${earningsDays}`),
      ]);
      setMine(m.data || []);
      setUnassigned(u.data || []);
      setHistory(h.data || null);
      setEarnings(e.data || null);
    } catch (e) {
      // Non-driver roles get 403 on the driver endpoints — harmless.
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningsDays]);

  // Broadcast GPS every 30 s for every "out for delivery" run. Only asks
  // for permission when the driver has at least one in-flight order.
  useEffect(() => {
    const inFlightIds = mine.filter((o) => o.driver_started_at).map((o) => o.id);
    if (inFlightIds.length === 0 || !navigator.geolocation) return;
    const push = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const body = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          inFlightIds.forEach((id) => {
            axios.post(`${API}/delivery/orders/${id}/location`, body).catch(() => {});
          });
        },
        () => {},                                  // silent — user denied or unavailable
        { enableHighAccuracy: true, maximumAge: 20000, timeout: 8000 },
      );
    };
    push();
    const t = setInterval(push, 30000);
    return () => clearInterval(t);
  }, [mine]);

  const claim = async (order) => {
    try {
      await axios.post(`${API}/delivery/orders/${order.id}/assign`, {});
      toast.success(`Took ${order.customer_name || "delivery"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't take order");
    }
  };

  const start = async (order) => {
    try {
      await axios.post(`${API}/delivery/orders/${order.id}/start`);
      toast.success("Out for delivery");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitComplete = async () => {
    if (!completing) return;
    try {
      const body = {};
      if (completing.tip !== "") body.tip_amount = Number(completing.tip);
      if (completing.mileage !== "") body.mileage = Number(completing.mileage);
      await axios.post(`${API}/delivery/orders/${completing.order.id}/complete`, body);
      toast.success("Delivered!");
      setCompleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const inFlight = useMemo(() => mine.filter((o) => o.driver_started_at), [mine]);
  const readyToGo = useMemo(() => mine.filter((o) => !o.driver_started_at), [mine]);

  return (
    <div className="min-h-screen bg-background" data-testid="driver-page">
      {/* Top bar */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Truck size={22} weight="bold" className="text-primary" />
            <div>
              <div className="font-heading text-lg font-medium">Driver</div>
              <div className="text-xs text-muted-foreground">{user.name}</div>
            </div>
          </div>
          <Button
            variant="outline" size="sm"
            className="gap-1 text-destructive border-destructive/30"
            onClick={doLogout}
            data-testid="driver-logout-btn"
          >
            <SignOut size={16} /> Out
          </Button>
        </div>
        <div className="max-w-4xl mx-auto flex px-4 gap-2 pb-2">
          {[
            { key: "mine", label: `My Deliveries (${mine.length})` },
            { key: "unassigned", label: `Available (${unassigned.length})` },
            { key: "earnings", label: "Earnings" },
            { key: "history", label: "History" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}
              data-testid={`driver-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto p-4 space-y-3">
        {tab === "mine" && (
          <>
            {mine.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm" data-testid="driver-mine-empty">
                Nothing assigned to you right now. Check the Available tab.
              </div>
            )}
            {readyToGo.length > 0 && (
              <section>
                <SectionTitle>Ready to leave</SectionTitle>
                {readyToGo.map((o) => (
                  <DeliveryCard key={o.id} order={o} accent="amber" testid={`mine-ready-${o.id}`}>
                    <Button
                      className="gap-2 w-full mt-2 bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={() => start(o)}
                      data-testid={`start-btn-${o.id}`}
                    >
                      <Truck size={16} weight="bold" /> Out for Delivery
                    </Button>
                  </DeliveryCard>
                ))}
              </section>
            )}
            {inFlight.length > 0 && (
              <section>
                <SectionTitle>Out for delivery</SectionTitle>
                {inFlight.map((o) => (
                  <DeliveryCard key={o.id} order={o} accent="blue" testid={`mine-inflight-${o.id}`}>
                    <div className="text-xs text-muted-foreground">Left at {fmtTime(o.driver_started_at)}</div>
                    <Button
                      className="gap-2 w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setCompleting({ order: o, tip: "", mileage: "" })}
                      data-testid={`complete-btn-${o.id}`}
                    >
                      Mark Delivered
                    </Button>
                  </DeliveryCard>
                ))}
              </section>
            )}
          </>
        )}

        {tab === "unassigned" && (
          <>
            {unassigned.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm" data-testid="driver-unassigned-empty">
                No deliveries waiting.
              </div>
            )}
            {unassigned.map((o) => (
              <DeliveryCard key={o.id} order={o} accent="slate" testid={`unassigned-${o.id}`}>
                <Button
                  className="gap-2 w-full mt-2"
                  onClick={() => claim(o)}
                  data-testid={`claim-btn-${o.id}`}
                >
                  Take This Order
                </Button>
              </DeliveryCard>
            ))}
          </>
        )}

        {tab === "earnings" && earnings && (
          <>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                Last {earnings.days} days
              </div>
              <div className="flex gap-1">
                {[7, 14, 30].map((d) => (
                  <button
                    key={d}
                    onClick={() => setEarningsDays(d)}
                    className={`px-2 py-1 rounded text-xs font-medium ${earningsDays === d ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                    data-testid={`earnings-range-${d}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="earnings-totals">
              <StatCard label="Total Take-Home" value={`$${earnings.totals.total.toFixed(2)}`} icon={<CurrencyDollar size={18} />} />
              <StatCard label="Deliveries" value={earnings.totals.deliveries} icon={<Truck size={18} />} />
              <StatCard label="Tips" value={`$${earnings.totals.tips.toFixed(2)}`} icon={<CurrencyDollar size={18} />} />
              <StatCard label="Mileage Pay" value={`$${earnings.totals.mileage_pay.toFixed(2)}`} icon={<Path size={18} />} />
              <StatCard label="Wages" value={`$${earnings.totals.wages.toFixed(2)}`} icon={<ClockClockwise size={18} />} />
              <StatCard label="Miles Driven" value={earnings.totals.miles.toFixed(1)} icon={<Path size={18} />} />
            </div>
            <div className="text-xs text-muted-foreground pl-1">
              Rates: ${earnings.hourly_rate.toFixed(2)}/hr · ${earnings.price_per_mile.toFixed(2)}/mi
            </div>
            {earnings.daily.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6" data-testid="earnings-empty">
                No completed deliveries or hours in this period yet.
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden text-xs">
                <div className="grid grid-cols-7 gap-2 p-2 bg-secondary/50 font-medium">
                  <div>Date</div>
                  <div className="text-right">Runs</div>
                  <div className="text-right">Tips</div>
                  <div className="text-right">Miles</div>
                  <div className="text-right">Mi Pay</div>
                  <div className="text-right">Wages</div>
                  <div className="text-right">Total</div>
                </div>
                {earnings.daily.map((r) => (
                  <div key={r.date} className="grid grid-cols-7 gap-2 p-2 border-t" data-testid={`earnings-day-${r.date}`}>
                    <div>{r.date}</div>
                    <div className="text-right">{r.deliveries}</div>
                    <div className="text-right">${r.tips.toFixed(2)}</div>
                    <div className="text-right">{r.miles.toFixed(1)}</div>
                    <div className="text-right">${r.mileage_pay.toFixed(2)}</div>
                    <div className="text-right">${r.wages.toFixed(2)}</div>
                    <div className="text-right font-semibold">${r.total.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "history" && history && (
          <>
            <div className="grid grid-cols-3 gap-2" data-testid="driver-history-totals">
              <StatCard label="Deliveries" value={history.count} icon={<Truck size={18} />} />
              <StatCard label="Tips" value={`$${history.total_tips.toFixed(2)}`} icon={<CurrencyDollar size={18} />} />
              <StatCard label="Miles" value={history.total_miles.toFixed(1)} icon={<Path size={18} />} />
            </div>
            {(history.orders || []).map((o) => (
              <div key={o.id} className="rounded-lg border p-3 text-sm" data-testid={`history-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium">{o.customer_name || "Delivery"}</div>
                  <div className="text-xs text-muted-foreground">{fmtTime(o.driver_delivered_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">{o.delivery_address}</div>
                <div className="text-xs flex gap-3 mt-1">
                  <span>${o.total?.toFixed(2)}</span>
                  {o.driver_tip_amount ? <span className="text-emerald-700 dark:text-emerald-300">+${o.driver_tip_amount.toFixed(2)} tip</span> : null}
                  {o.driver_mileage ? <span>{o.driver_mileage.toFixed(1)} mi</span> : null}
                </div>
              </div>
            ))}
            {(history.orders || []).length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm">
                No delivered orders yet. Go grab one!
              </div>
            )}
          </>
        )}
      </main>

      {/* Complete-delivery dialog: capture tip + mileage */}
      <Dialog open={!!completing} onOpenChange={(v) => !v && setCompleting(null)}>
        <DialogContent data-testid="complete-delivery-dialog">
          <DialogHeader>
            <DialogTitle>Delivered!</DialogTitle>
            <DialogDescription>
              Optional — log any cash tip and mileage. Card tips are already tracked on the order.
            </DialogDescription>
          </DialogHeader>
          {completing && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-medium">{completing.order.customer_name || "Delivery"}</div>
                <div className="text-xs text-muted-foreground">{completing.order.delivery_address}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>Cash Tip ($)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={completing.tip}
                    onChange={(e) => setCompleting({ ...completing, tip: e.target.value })}
                    data-testid="complete-tip-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Miles Driven</Label>
                  <Input
                    type="number" step="0.1" min="0"
                    value={completing.mileage}
                    onChange={(e) => setCompleting({ ...completing, mileage: e.target.value })}
                    data-testid="complete-mileage-input"
                    placeholder={completing.order.delivery_distance_mi ? `~${completing.order.delivery_distance_mi.toFixed(1)}` : ""}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(null)}>Cancel</Button>
            <Button onClick={submitComplete} data-testid="complete-submit-btn">Save & Mark Delivered</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1 mt-2">
    {children}
  </div>
);

const StatCard = ({ label, value, icon }) => (
  <div className="rounded-lg border p-3 flex items-center gap-2">
    <div className="text-primary">{icon}</div>
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-lg font-heading font-medium">{value}</div>
    </div>
  </div>
);

const DeliveryCard = ({ order, accent, testid, children }) => {
  const tones = {
    amber: "border-amber-500/40 bg-amber-500/5",
    blue: "border-blue-500/40 bg-blue-500/5",
    slate: "border-border",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[accent]}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {order.customer_name || "Delivery"}{" "}
            <span className="text-xs text-muted-foreground">#{order.id.slice(-6)}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin size={12} /> <span className="truncate">{order.delivery_address}</span>
          </div>
          {order.customer_phone && (
            <a href={`tel:${order.customer_phone}`} className="flex items-center gap-1 text-xs text-primary mt-1">
              <Phone size={12} /> {order.customer_phone}
            </a>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-muted-foreground">Order</div>
          <div className="font-mono font-medium">${order.total?.toFixed(2)}</div>
          {order.delivery_distance_mi != null && (
            <div className="text-[10px] text-muted-foreground">{order.delivery_distance_mi.toFixed(1)} mi</div>
          )}
        </div>
      </div>
      {order.estimated_delivery_at && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <ClockClockwise size={12} /> ETA {fmtTime(order.estimated_delivery_at)}
        </div>
      )}
      {(order.items || []).length > 0 && (
        <div className="text-xs text-muted-foreground mt-1">
          {order.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}
        </div>
      )}
      {order.delivery_notes && (
        <div className="text-xs italic mt-1">Note: {order.delivery_notes}</div>
      )}
      {children}
    </div>
  );
};

export default DriverPage;
